import { NavigationOption } from "@intelligems/ig-types";

import type { AppListenerApi } from "@src/store/rootState";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { resolveFullUrl } from "@src/store/slices/experienceBuilder/common/utils/resolveFullUrl";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { getOrganization } from "@src/store/slices/organization";

import { matchUiAction } from "../detect";
import { Continuation, Matcher, UiActionResult } from "../types";
import { waitForPageSettle } from "./settle";

// Concrete-slice imports (never the `ExperienceBuilder*Action` aggregator —
// that causes circular init in redux code).
const { _setTargetUrl } = experienceBuilderSlice.actions;
const { getDemoUrl } = experienceBuilderSlice.selectors;
const {
  showNavigationChoices,
  navigationChoiceMade,
  navigationChoiceCancelled,
  clearNavigationChoice,
} = experienceBuilderAiSlice.actions;
const { getNavigationChoice } = experienceBuilderAiSlice.selectors;

/**
 * Point the preview at `path` on whichever store is being previewed — the
 * configured demo store when there is one, otherwise the org's own.
 *
 * Exported so it can be unit-tested without driving the full effect.
 */
export const navigateTo = (path: string, api: AppListenerApi) => {
  const org = getOrganization(api.getState());
  const demoUrl = getDemoUrl(api.getState());
  api.dispatch(_setTargetUrl(resolveFullUrl(path, org, demoUrl)));
};

/**
 * The follow-up once a navigate has settled. The picker branch reads (and
 * clears) the merchant's choice; the direct branch just reports completion.
 * Exported so it can be unit-tested without driving the full effect.
 */
export function navigateContinuation(
  results: UiActionResult,
  api: AppListenerApi
): Continuation {
  if (Array.isArray(results.options)) {
    const choice = getNavigationChoice(api.getState());
    api.dispatch(clearNavigationChoice());
    if (choice?.resolution?.kind === "selected") {
      return {
        kind: "user",
        message: `[system] Navigated to ${choice.resolution.option.value}. Fresh page HTML is provided in variables.`,
      };
    }
    return {
      kind: "user",
      message:
        "[system] The user cancelled navigation — you are still on the current page. Do not retry navigation unless they ask.",
    };
  }
  return {
    kind: "user",
    message: `[system] Navigation complete. Current path: ${results.path}. Fresh page HTML is provided in variables.`,
  };
}

export const navigate: Matcher = {
  // Two shapes (mutually exclusive, per the navigate tool):
  //   `path`    → navigate straight there, wait for the page to settle.
  //   `options` → hand the merchant a picker; await their choice, then
  //               navigate to it (or stay put on cancel). Either way the
  //               continuation reports the outcome; the AI never re-navigates.
  match: (part) => {
    const results = matchUiAction(part, "navigate");
    if (!results) return null;
    return async (api) => {
      if (Array.isArray(results.options)) {
        api.dispatch(
          showNavigationChoices(results.options as NavigationOption[])
        );
        await api.take(
          (action) =>
            navigationChoiceMade.match(action) ||
            navigationChoiceCancelled.match(action)
        );
        const resolved = getNavigationChoice(api.getState());
        if (resolved?.resolution?.kind === "selected") {
          navigateTo(resolved.resolution.option.value, api);
          await waitForPageSettle(api);
        }
      } else {
        navigateTo(results.path as string, api);
        await waitForPageSettle(api);
      }
      return navigateContinuation(results, api);
    };
  },
};
